import { useCallback, useEffect, useRef, useState } from 'react';

const TITLE_COMMIT_DELAY_MS = 400;

export interface UseTitleDraftOptions {
  /** The draft belongs to this report; a change discards it uncommitted. */
  reportId: string;
  /** The committed title, shown whenever nothing is being typed. */
  title: string;
  commit(value: string): void;
}

export interface TitleDraft {
  /** What the title field shows: the draft while typing, else `title`. */
  shown: string;
  onChange(value: string): void;
  /** Commits the draft now (Enter, blur). */
  flush(): void;
}

type Draft = { reportId: string; value: string };

/**
 * The title field's debounced draft: typing commits after 400ms of quiet,
 * Enter and blur commit at once, and so does unmounting — leaving the screen
 * mid-debounce (say, keyboard back-navigation, with no blur) keeps what was
 * typed.
 *
 * A report id change is different: it discards the draft uncommitted. The
 * route keys `Workspace` by report id, so navigating to another report
 * already mounts a fresh draft; tagging the draft with the report it was
 * typed for is defence in depth, so a draft can never reappear as — or be
 * committed to — another report's title.
 */
export function useTitleDraft(options: UseTitleDraftOptions): TitleDraft {
  const { reportId, title, commit } = options;
  const [draft, setDraft] = useState<Draft | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The uncommitted draft, read by every commit path and cleared by the
  // first, so a timer, a blur and an unmount between them commit it once.
  const pendingRef = useRef<Draft | null>(null);
  const reportIdRef = useRef(reportId);
  reportIdRef.current = reportId;
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const cancelTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const commitPending = useCallback(() => {
    cancelTimer();
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending && pending.reportId === reportIdRef.current) commitRef.current(pending.value);
  }, [cancelTimer]);

  // A report id change cancels the pending commit; the tag above discards the draft.
  useEffect(() => cancelTimer, [reportId, cancelTimer]);
  useEffect(() => commitPending, [commitPending]);

  const onChange = useCallback(
    (value: string) => {
      pendingRef.current = { reportId, value };
      setDraft({ reportId, value });
      cancelTimer();
      timer.current = setTimeout(() => {
        timer.current = null;
        commitPending();
        setDraft(null);
      }, TITLE_COMMIT_DELAY_MS);
    },
    [cancelTimer, commitPending, reportId]
  );

  const flush = useCallback(() => {
    commitPending();
    setDraft(null);
  }, [commitPending]);

  const current = draft !== null && draft.reportId === reportId ? draft.value : null;
  return { shown: current ?? title, onChange, flush };
}
